// src/pages/synthesize/index.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SourceInputCard from './components/SourceInputCard';
import AnglePicker from './components/AnglePicker';
import { synthesizeArticle } from '../../services/synthesisService';
import EditPreviewModal from '../idea-refiner/components/EditPreviewModal';
import UpgradeModal from '../../components/ui/UpgradeModal';
import { publishToLinkedIn, publishToTwitter, validateToken, getUserTokens } from '../../services/publishService';
import { supabase } from '../../lib/supabase';
import { friendlyError } from '../../utils/errorMessages';
import { canUseFeature } from '../../services/subscriptionService';
import { createScheduledPost } from '../../services/scheduleService';

const LAST_ANGLE_KEY = 'last_synthesis_angle';

export default function SynthesizePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, subscription, refreshUsage } = useAuth();
  const toast = useToast();

  // ── URL param prefill ──
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const prefillUrl = searchParams.get('url') || '';
  const prefillText = searchParams.get('text') || '';

  const [mode, setMode] = useState(prefillText ? 'paste' : 'url');
  const [url, setUrl] = useState(prefillUrl);
  const [text, setText] = useState(prefillText);
  const [angle, setAngle] = useState(() => {
    try {
      const stored = localStorage.getItem(LAST_ANGLE_KEY);
      return stored || '';
    } catch { return ''; }
  });
  const [freeformText, setFreeformText] = useState('');
  const [loading, setLoading] = useState(false);
  const [progressLabel, setProgressLabel] = useState('');

  const [result, setResult] = useState(null); // { linkedin, x_thread, source_metadata, synthesis_id }
  const [showPreview, setShowPreview] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeData, setUpgradeData] = useState({});

  // ── Content state (editable in modal) ──
  const [linkedinContent, setLinkedinContent] = useState('');
  const [twitterContent, setTwitterContent] = useState('');
  const [xThreadContent, setXThreadContent] = useState(null);
  const [postAsThread, setPostAsThread] = useState(true);

  // ── Platform & publishing ──
  const [selectedPlatforms, setSelectedPlatforms] = useState({ linkedin: true, twitter: true });
  const [tokens, setTokens] = useState({ linkedin: null, twitter: null });
  const [tokenStatus, setTokenStatus] = useState({ linkedin: 'checking', twitter: 'checking' });
  const [publishing, setPublishing] = useState({ linkedin: false, twitter: false, unified: false });
  const [publishedPlatforms, setPublishedPlatforms] = useState({ linkedin: false, twitter: false });
  const [publishedPostIds, setPublishedPostIds] = useState({ linkedin: null, twitter: null });
  const [contentPostId, setContentPostId] = useState(null);

  // ── Images ──
  const [uploadedImages, setUploadedImages] = useState({ linkedin: [], twitter: [] });
  const [imagePreviewUrls, setImagePreviewUrls] = useState({ linkedin: [], twitter: [] });

  // ── Scheduling ──
  const [scheduling, setScheduling] = useState(false);
  const [scheduledInfo, setScheduledInfo] = useState(null);

  // ── Auth gate: preserve query, redirect to /login with next ──
  useEffect(() => {
    if (!user) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [user, location.pathname, location.search, navigate]);

  // ── Check tokens on mount ──
  useEffect(() => {
    if (!userProfile?.id) return;
    const checkTokens = async () => {
      try {
        const userTokens = await getUserTokens(userProfile?.id);
        setTokens(userTokens);
        for (const p of ['linkedin', 'twitter']) {
          if (userTokens?.[p]?.token) {
            const v = await validateToken(p, userTokens[p].token);
            setTokenStatus(prev => ({ ...prev, [p]: v?.valid ? 'valid' : 'invalid' }));
          } else {
            setTokenStatus(prev => ({ ...prev, [p]: 'missing' }));
          }
        }
      } catch { setTokenStatus({ linkedin: 'error', twitter: 'error' }); }
    };
    checkTokens();
  }, [userProfile]);

  // ── Persist last-used angle ──
  useEffect(() => {
    if (angle && angle !== 'freeform') {
      try { localStorage.setItem(LAST_ANGLE_KEY, angle); } catch {}
    }
  }, [angle]);

  // ── Save content to dashboard (ported from idea-refiner, with article_synthesis_id added) ──
  const saveContentToDashboard = useCallback(async (articleSynthesisId) => {
    try {
      const { data: { user: currentUser } } = await supabase?.auth?.getUser();
      if (!currentUser) throw new Error('User not authenticated');

      const hasThread = Array.isArray(xThreadContent) && xThreadContent.length > 0;
      const { data, error } = await supabase?.from('content_posts')?.insert({
        user_id: currentUser.id,
        original_idea: result?.source_metadata?.title || result?.source_metadata?.domain || 'Article synthesis',
        linkedin_content: linkedinContent || null,
        twitter_content: hasThread ? null : (twitterContent || null),
        x_thread_content: hasThread ? xThreadContent : null,
        ai_model: 'default',
        writing_style: `synthesis:${angle}`,
        ...(articleSynthesisId ? { article_synthesis_id: articleSynthesisId } : {}),
      })?.select()?.maybeSingle();

      if (error) {
        console.error('Error saving synthesis content:', error);
        return null;
      }
      setContentPostId(data?.id);
      return data?.id;
    } catch (err) {
      console.error('Error saving:', err);
      return null;
    }
  }, [linkedinContent, twitterContent, xThreadContent, result, angle]);

  // ── Image handlers ──
  const handleImagesUploaded = (platform, newImages) => {
    setUploadedImages(prev => ({ ...prev, [platform]: [...(prev[platform] || []), ...newImages] }));
  };
  const handleImagePreviewAdded = (platform, previews, replace = false) => {
    setImagePreviewUrls(prev => ({ ...prev, [platform]: replace ? previews : [...(prev[platform] || []), ...previews] }));
  };
  const handleImageRemoved = (platform, index) => {
    setUploadedImages(prev => ({ ...prev, [platform]: (prev[platform] || []).filter((_, i) => i !== index) }));
    setImagePreviewUrls(prev => ({ ...prev, [platform]: (prev[platform] || []).filter((_, i) => i !== index) }));
  };

  // ── Publishing handlers (ported from idea-refiner) ──
  const handlePublish = async (platform) => {
    if (publishedPlatforms?.[platform]) {
      toast.warning(`Already published to ${platform === 'linkedin' ? 'LinkedIn' : 'X'}.`);
      return;
    }
    setPublishing(prev => ({ ...prev, [platform]: true }));
    try {
      let postId = contentPostId;
      if (!postId) postId = await saveContentToDashboard(result?.synthesis_id);
      if (tokenStatus?.[platform] !== 'valid') throw new Error(`Please connect your ${platform === 'linkedin' ? 'LinkedIn' : 'X'} account first`);

      const imgs = uploadedImages[platform] || [];
      let publishResult;
      if (platform === 'linkedin') {
        publishResult = await publishToLinkedIn(linkedinContent, tokens?.linkedin?.token, postId, imgs);
      } else {
        const xPayload = xThreadContent && postAsThread
          ? xThreadContent.filter(t => t.trim().length > 0)
          : (xThreadContent ? xThreadContent[0] : twitterContent);
        publishResult = await publishToTwitter(xPayload, tokens?.twitter?.token, postId, imgs);
      }

      setPublishedPlatforms(prev => ({ ...prev, [platform]: true }));
      if (publishResult?.postId) setPublishedPostIds(prev => ({ ...prev, [platform]: publishResult.postId }));
      toast.success(`Published to ${platform === 'linkedin' ? 'LinkedIn' : 'X'} successfully!`);
      refreshUsage?.({ publishes: 1 });
    } catch (error) {
      if (error?.type === 'THREAD_PARTIAL_FAILURE') {
        toast.error(
          `Published tweets 1-${error.published_up_to} of ${error.total}. ` +
          `The rest didn't post — open the modal again to copy them.`
        );
      } else if (error?.type === 'LIMIT_REACHED' || error?.message?.includes('limit_reached')) {
        setUpgradeData({ feature: 'publish', used: error.used ?? 0, limit: error.limit ?? 3, daysUntilReset: error.daysUntilReset ?? 30, message: error.message });
        setShowUpgrade(true);
      } else {
        toast.error(friendlyError(error));
      }
    } finally {
      setPublishing(prev => ({ ...prev, [platform]: false }));
    }
  };

  const handleUnifiedPublish = async () => {
    if (tokenStatus?.linkedin !== 'valid' && tokenStatus?.twitter !== 'valid') { toast.error('Connect at least one account.'); return; }
    setPublishing(prev => ({ ...prev, unified: true }));
    try {
      let postId = contentPostId;
      if (!postId) postId = await saveContentToDashboard(result?.synthesis_id);
      const promises = [];
      if (selectedPlatforms?.linkedin && tokenStatus?.linkedin === 'valid' && linkedinContent) {
        promises.push(publishToLinkedIn(linkedinContent, tokens?.linkedin?.token, postId, uploadedImages.linkedin || []).then(() => ({ platform: 'linkedin', success: true })).catch(e => ({ platform: 'linkedin', success: false, error: e })));
      }
      const xPayload = xThreadContent && postAsThread
        ? xThreadContent.filter(t => t.trim().length > 0)
        : (xThreadContent ? xThreadContent[0] : twitterContent);
      if (selectedPlatforms?.twitter && tokenStatus?.twitter === 'valid' && xPayload) {
        promises.push(publishToTwitter(xPayload, tokens?.twitter?.token, postId, uploadedImages.twitter || []).then(() => ({ platform: 'twitter', success: true })).catch(e => ({ platform: 'twitter', success: false, error: e })));
      }
      const results = await Promise.all(promises);
      let ok = 0;
      results.forEach(({ platform, success }) => { if (success) { ok++; setPublishedPlatforms(prev => ({ ...prev, [platform]: true })); } });
      if (ok === results.length) toast.success(`Published to ${ok} platform${ok > 1 ? 's' : ''}!`);
      else if (ok > 0) toast.warning(`Published to ${ok}/${results.length} platforms.`);
      else toast.error('Publishing failed.');
      refreshUsage?.(ok > 0 ? { publishes: 1 } : null);
    } catch (error) { toast.error(friendlyError(error)); }
    finally { setPublishing(prev => ({ ...prev, unified: false })); }
  };

  // ── Schedule handler (ported from idea-refiner) ──
  const handleSchedule = async (scheduledAt, timezone, schedulePlatforms) => {
    if (!canUseFeature('schedule', subscription)) {
      setUpgradeData({
        feature: 'schedule',
        used: subscription?.usage?.scheduled_posts ?? 0,
        limit: subscription?.limits?.scheduled_posts_per_month ?? 1,
        daysUntilReset: subscription?.daysUntilReset ?? 30,
      });
      setShowUpgrade(true);
      return;
    }

    const platformsToSchedule = schedulePlatforms || selectedPlatforms;
    setScheduling(true);
    try {
      let postId = contentPostId;
      if (!postId) postId = await saveContentToDashboard(result?.synthesis_id);

      const imageData = [
        ...(platformsToSchedule.linkedin ? (uploadedImages.linkedin || []).map(img => ({ ...img, platform: 'linkedin' })) : []),
        ...(platformsToSchedule.twitter ? (uploadedImages.twitter || []).map(img => ({ ...img, platform: 'twitter' })) : []),
      ];

      const xPayload = xThreadContent && postAsThread
        ? xThreadContent.filter(t => t.trim().length > 0)
        : (xThreadContent ? xThreadContent[0] : twitterContent);

      await createScheduledPost({
        linkedinContent: platformsToSchedule.linkedin ? linkedinContent : null,
        twitterContent: platformsToSchedule.twitter ? xPayload : null,
        platforms: platformsToSchedule,
        scheduledAt,
        timezone,
        contentPostId: postId,
        imageData,
      });

      const platformNames = [platformsToSchedule.linkedin && 'LinkedIn', platformsToSchedule.twitter && 'X'].filter(Boolean).join(' & ');
      setScheduledInfo({ scheduledAt, timezone, platforms: platformsToSchedule });
      toast.success(`Post scheduled to ${platformNames} for ${new Date(scheduledAt).toLocaleDateString([], { month: 'short', day: 'numeric' })} at ${new Date(scheduledAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`);
      refreshUsage?.({ scheduled_posts: 1 });
    } catch (error) {
      if (error?.code === 'limit_reached') {
        setUpgradeData({
          feature: 'schedule',
          used: error.used ?? 0,
          limit: error.limit ?? 1,
          daysUntilReset: error.daysUntilReset ?? 30,
          message: error.message,
        });
        setShowUpgrade(true);
      } else {
        toast.error(friendlyError(error));
      }
    } finally {
      setScheduling(false);
    }
  };

  const canGenerate =
    !loading &&
    angle !== '' &&
    (mode === 'url' ? url.trim().length > 0 : text.trim().length >= 100) &&
    (angle !== 'freeform' || freeformText.trim().length >= 5);

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setLoading(true);
    setProgressLabel(mode === 'url' ? 'Reading the article…' : 'Synthesizing your take…');

    // Reset publish/schedule state for new generation
    setPublishedPlatforms({ linkedin: false, twitter: false });
    setPublishedPostIds({ linkedin: null, twitter: null });
    setContentPostId(null);
    setScheduledInfo(null);
    setUploadedImages({ linkedin: [], twitter: [] });
    setImagePreviewUrls({ linkedin: [], twitter: [] });

    try {
      const args = {
        synthesisAngle: angle,
        freeformAngleText: angle === 'freeform' ? freeformText.trim() : undefined,
      };
      if (mode === 'url') args.sourceUrl = url.trim();
      else args.sourceText = text.trim();

      setProgressLabel('Synthesizing your take…');
      const res = await synthesizeArticle(args);
      setResult(res);
      setLinkedinContent(res.linkedin);
      setXThreadContent(Array.isArray(res.x_thread) ? res.x_thread : null);
      setTwitterContent('');
      setPostAsThread(true);
      setShowPreview(true);
      refreshUsage?.({ ai_generations: 1 });
    } catch (err) {
      if (err?.type === 'LIMIT_REACHED') {
        setUpgradeData({
          feature: 'ai_generate',
          used: err.used ?? 0,
          limit: err.limit ?? 5,
          daysUntilReset: err.daysUntilReset ?? 30,
          message: err.message,
        });
        setShowUpgrade(true);
      } else if (err?.type === 'fetch_failed' || err?.type === 'unsupported_source' || err?.type === 'too_short') {
        toast.error(err.message);
        setMode('paste'); // auto-switch to paste tab
      } else {
        toast.error(err?.message || 'Synthesis failed. Try again.');
      }
    } finally {
      setLoading(false);
      setProgressLabel('');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <main className="px-4 md:px-6 lg:px-8 pb-12 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col justify-center">

          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
              Synthesize from an article
            </h1>
            <p className="text-sm text-muted-foreground">
              Paste a URL or article text, pick your angle, get a LinkedIn post + X thread in your voice.
            </p>
          </div>

          {/* Card */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-6">
            <SourceInputCard
              mode={mode}
              onModeChange={setMode}
              url={url}
              onUrlChange={setUrl}
              text={text}
              onTextChange={setText}
            />

            <AnglePicker
              value={angle}
              onChange={setAngle}
              freeformText={freeformText}
              onFreeformChange={setFreeformText}
            />

            <div>
              <Button
                variant="primary"
                size="lg"
                onClick={handleGenerate}
                disabled={!canGenerate}
                className="w-full"
              >
                {loading ? (
                  <span className="inline-flex items-center gap-2">
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    {progressLabel || 'Working…'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2">
                    <Icon name="Sparkles" size={16} />
                    Synthesize
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Source metadata footer when result exists */}
          {result?.source_metadata?.domain && (
            <p className="text-xs text-muted-foreground text-center mt-3">
              Source: {result.source_metadata.title || result.source_metadata.domain}
              {result.source_metadata.fetched_via === 'jina' && ' (via fallback reader)'}
            </p>
          )}
        </div>
      </main>

      {showPreview && result && (
        <EditPreviewModal
          isOpen={showPreview}
          onClose={() => setShowPreview(false)}
          linkedinContent={linkedinContent}
          setLinkedinContent={setLinkedinContent}
          twitterContent={twitterContent}
          setTwitterContent={setTwitterContent}
          selectedPlatforms={selectedPlatforms}
          setSelectedPlatforms={setSelectedPlatforms}
          tokenStatus={tokenStatus}
          tokens={tokens}
          uploadedImages={uploadedImages}
          imagePreviewUrls={imagePreviewUrls}
          onImagesUploaded={handleImagesUploaded}
          onImagePreviewAdded={handleImagePreviewAdded}
          onImageRemoved={handleImageRemoved}
          contentPostId={contentPostId}
          saveContentToDashboard={() => saveContentToDashboard(result?.synthesis_id)}
          publishing={publishing}
          publishedPlatforms={publishedPlatforms}
          publishedPostIds={publishedPostIds}
          onPublish={handlePublish}
          onUnifiedPublish={handleUnifiedPublish}
          onRegenerate={() => {}}
          isRegenerating={false}
          regenDisabled={true}
          regenDisabledReason="Use Synthesize again with a different angle to regenerate."
          onSchedule={handleSchedule}
          scheduling={scheduling}
          scheduledInfo={scheduledInfo}
          xThreadContent={xThreadContent}
          onXThreadChange={setXThreadContent}
          onPostAsThreadChange={setPostAsThread}
        />
      )}

      {showUpgrade && (
        <UpgradeModal
          isOpen={showUpgrade}
          onClose={() => setShowUpgrade(false)}
          feature={upgradeData.feature}
          used={upgradeData.used}
          limit={upgradeData.limit}
          daysUntilReset={upgradeData.daysUntilReset}
          message={upgradeData.message}
        />
      )}
    </div>
  );
}
